import type { AnalyticsEventName, AnalyticsEventProps } from './analyticsEvents'

/**
 * Мост в Яндекс.Метрику. Свой event-store остаётся источником истины — Метрика нужна
 * ради того, чего у нас нет: карт кликов и скроллов, Вебвизора и отчётов по источникам
 * трафика. Поэтому сюда уезжают только ключевые шаги, а не весь поток событий:
 * сотня целей превращает интерфейс Метрики в свалку.
 */

/** Имена целей совпадают с именами событий — цель в Метрике заводится один в один. */
const GOALS: ReadonlySet<AnalyticsEventName> = new Set<AnalyticsEventName>([
  'add_to_cart',
  'auth_success',
  'checkout_submit',
  'push_opened',
])

let counterId = 0

/**
 * Выбирает счётчик под текущую сборку. У приложения он свой: WebView шлёт хиты
 * с capacitor://localhost, и в счётчике сайта они ломают отчёты по доменам и Вебвизор.
 * Пустое значение = метрика для этой сборки выключена.
 */
export const resolveYmCounterId = (params: {
  buildTarget?: string
  ymCounterId?: string
  ymCounterIdMobile?: string
}): number => {
  const raw = params.buildTarget === 'mobile' ? params.ymCounterIdMobile : params.ymCounterId
  const parsed = Number(raw)
  return raw && !Number.isNaN(parsed) ? parsed : 0
}

const isReady = (): boolean =>
  counterId > 0 && typeof window !== 'undefined' && typeof window.ym === 'function'

const pushEcommerce = (payload: Record<string, unknown>): void => {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) return
  window.dataLayer.push({ ecommerce: { currencyCode: 'RUB', ...payload } })
}

/**
 * Ecommerce-отчёты Метрики понимают фиксированные шаги (detail → add → purchase),
 * поэтому событие в них переводим отдельно от целей.
 */
const reportEcommerce = <N extends AnalyticsEventName>(
  name: N,
  props: AnalyticsEventProps<N>,
): void => {
  if (name === 'product_modal_open') {
    const p = props as AnalyticsEventProps<'product_modal_open'>
    pushEcommerce({
      detail: {
        products: [{ id: String(p.productId), name: p.productName, price: p.price }],
      },
    })
    return
  }

  if (name === 'add_to_cart') {
    const p = props as AnalyticsEventProps<'add_to_cart'>
    pushEcommerce({
      add: {
        products: [
          {
            id: String(p.productId),
            name: p.productName,
            price: p.price,
            quantity: p.quantity,
          },
        ],
      },
    })
    return
  }

  if (name === 'payment_result') {
    const p = props as AnalyticsEventProps<'payment_result'>
    // Покупкой считаем только успешную оплату: webhook провайдера — единственная истина
    // по деньгам, отправка формы заказа ею не является.
    if (!p.ok || !p.orderId) return
    pushEcommerce({ purchase: { actionField: { id: String(p.orderId) } } })
  }
}

export const initYmAdapter = (id: number): void => {
  counterId = id
}

export const reportToYm = <N extends AnalyticsEventName>(
  name: N,
  props: AnalyticsEventProps<N>,
): void => {
  if (!isReady()) return

  try {
    if (GOALS.has(name)) {
      window.ym(counterId, 'reachGoal', name, props as Record<string, unknown>)
    }
    reportEcommerce(name, props)
  } catch {
    // Метрика недоступна (блокировщик, оффлайн) — на сценарий пользователя не влияет.
  }
}
